import { joinPath } from '@eventbrite/path-utils';
import { replace } from 'react-router-redux';
import { Dispatch } from 'redux';
import {
    LoggedInUserData,
    transformCurrentUserData,
    transformUserSiginMethods,
    UserDataResponse,
    UserSignInMethodsData,
} from '../api/transformations/user';
import { getCurrentUserAccountDetails } from '../api/users';
import { BASE_URL, CHECKOUT_PATH } from '../constants';
import { UserInstrument, VirtualIncentive } from '../models/user';
import { CheckoutState } from '../state';
import { UserNamespace, WithUserNamespace } from '../state/user';
import {
    getLoginWindowPopUpInstance,
    showNewLoginComponent,
} from '../utils/login';
import { trackLoginContinueAsGuestClicked } from './eventAnalytics';
import { createOrder, getAndSaveAvailablePaymentMethods } from './order/common';

export enum UserActionType {
    updateUserInstruments = 'updateUserInstruments',
    setVirtualIncentive = 'setVirtualIncentive',
    setUserAsLoggedIn = 'setUserAsLoggedIn',
    setUserSiginInMethods = 'setUserSiginInMethods',
    clearUserData = 'clearUserData',
    initializeUser = 'initializeUser',
    setLoginFromCheckoutPage = 'setLoginFromCheckoutPage',
    setLoginWindowPopUpInstance = 'setLoginWindowPopUpInstance',
}

export interface InitializeUserAction {
    type: UserActionType.initializeUser;
    payload?: UserNamespace;
}

export interface UpdateUserInstrumentsAction {
    type: UserActionType.updateUserInstruments;
    payload: Array<UserInstrument>;
}

export interface SetVirtualIncentiveAction {
    type: UserActionType.setVirtualIncentive;
    payload: VirtualIncentive;
}

export interface SetUserAsLoggedInAction {
    type: UserActionType.setUserAsLoggedIn;
    payload: LoggedInUserData;
}

export interface SetUserSiginInMethodsAction {
    type: UserActionType.setUserSiginInMethods;
    payload: UserSignInMethodsData;
}

export interface ClearUserDataAction {
    type: UserActionType.clearUserData;
}

export interface SetLoginFromCheckoutPageAction {
    type: UserActionType.setLoginFromCheckoutPage;
    payload: boolean;
}

export interface SetLoginWindowPopUpInstanceAction {
    type: UserActionType.setLoginWindowPopUpInstance;
    payload: Window | null;
}

export const getUpdateUserInstrumentsAction = (
    userInstruments: Array<UserInstrument>,
): UpdateUserInstrumentsAction => ({
    type: UserActionType.updateUserInstruments,
    payload: userInstruments,
});

export const setUserAsLoggedInAction = (
    userDataResponse: UserDataResponse,
): SetUserAsLoggedInAction => ({
    type: UserActionType.setUserAsLoggedIn,
    payload: transformCurrentUserData(userDataResponse),
});

export const setVirtualIncentiveAction = (
    virtualIncentive: VirtualIncentive,
): SetVirtualIncentiveAction => ({
    type: UserActionType.setVirtualIncentive,
    payload: virtualIncentive,
});

export const setUserSiginInMethodsAction = (
    signInMethods: Array<string>,
): SetUserSiginInMethodsAction => ({
    type: UserActionType.setUserSiginInMethods,
    payload: transformUserSiginMethods(signInMethods),
});

export const clearUserDataAction = (): ClearUserDataAction => ({
    type: UserActionType.clearUserData,
});

export const initializeUserAction = (
    initialData: WithUserNamespace,
): InitializeUserAction => ({
    type: UserActionType.initializeUser,
    payload: initialData.user,
});

export const setLoginFromCheckoutPage = (
    fromCheckoutPage: boolean,
): SetLoginFromCheckoutPageAction => ({
    type: UserActionType.setLoginFromCheckoutPage,
    payload: fromCheckoutPage,
});

export const setLoginWindowPopUpInstance = (
    windowPopUpInstance: Window | null,
): SetLoginWindowPopUpInstanceAction => ({
    type: UserActionType.setLoginWindowPopUpInstance,
    payload: windowPopUpInstance,
});

export const handleLoggedInUserInfo = () => async (dispatch: Dispatch<any>) => {
    const { response } = await getCurrentUserAccountDetails();

    dispatch(setUserAsLoggedInAction(response));
};

export const handleLoginSuccessful =
    () => async (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();

        await dispatch(handleLoggedInUserInfo());

        if (state.user?.loginFromCheckoutPage) {
            await dispatch(getAndSaveAvailablePaymentMethods());
            dispatch(replace(joinPath(BASE_URL, CHECKOUT_PATH)));
        } else {
            await dispatch(createOrder({ shouldRedirect: true }));
            await dispatch(getAndSaveAvailablePaymentMethods());
        }
    };

export const handleOpenLoginPopUp =
    () => async (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const { app } = getState();

        if (!showNewLoginComponent(app.isEventbriteTLD)) {
            const loginWindowPopUpInstance = getLoginWindowPopUpInstance(
                app.locale,
            );
            dispatch(setLoginWindowPopUpInstance(loginWindowPopUpInstance));
        }
    };

export const handleCloseLoginPopUp =
    () => async (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const { app } = getState();

        if (!showNewLoginComponent(app.isEventbriteTLD)) {
            dispatch(setLoginWindowPopUpInstance(null));
        }
    };

export const handleContinueAsGuest =
    (isPasswordDisclaimerShown: boolean) =>
    async (dispatch: Dispatch<any>, getState: () => CheckoutState) => {
        const state = getState();

        dispatch(trackLoginContinueAsGuestClicked(isPasswordDisclaimerShown));

        if (state.user?.loginFromCheckoutPage) {
            dispatch(replace(joinPath(BASE_URL, CHECKOUT_PATH)));
        } else {
            await dispatch(createOrder({ shouldRedirect: true }));
        }
    };

export type UserAction =
    | UpdateUserInstrumentsAction
    | SetUserAsLoggedInAction
    | SetVirtualIncentiveAction
    | SetUserSiginInMethodsAction
    | ClearUserDataAction
    | InitializeUserAction
    | SetLoginFromCheckoutPageAction
    | SetLoginWindowPopUpInstanceAction;
